import React from 'react';
import {TimkenGearApex, TimkenGearDrive, TimkenGearRotation, TimkenHand, TimkenTorqueOrPower} from './types';
import {handleActionError} from '../utils/engineering-tools';
import {TimkenUnit} from '../../components/bearing-search/types/types';
import {isValidDegAngle} from '../utils/validations';

type GearForceValidationMap = Map<keyof GearForceState, boolean>;

export type GearForceState = {
	apex?: TimkenGearApex | '';
	centerDistance?: number | undefined;
	concaveAngle?: number | undefined;
	convexAngle?: number | undefined;
	directionRotation: TimkenGearRotation | undefined | '';
	efficiency?: number | undefined;
	faceAngle?: number | undefined;
	gearDriveOptions: TimkenGearDrive | undefined;
	gearName: string | undefined;
	gearType: string | undefined;
	hand?: TimkenHand | undefined;
	helixAngle?: number | undefined;
	leadAngle?: number | undefined;
	meshAngle: number | undefined;
	pitchAngle?: number | undefined;
	pressureAngle?: number | undefined;
	ratio?: number | undefined;
	rootAngle?: number | undefined;
	speed: number | undefined;
	spiralAngle?: number | undefined;
	torque: number | undefined;
	torqueOrPower: TimkenTorqueOrPower | '';
	unit: TimkenUnit;
	validation: GearForceValidationMap;
	workingDiameter: number | undefined;
};


// gearType IDs are 0 to 10
export const mapFieldsPerGearType = {
	apex: [3, 4, 9, 10],
	centerDistance: [7, 8],
	concaveAngle: [9, 10],
	convexAngle: [9, 10],
	efficiency: [5, 6, 7, 8],
	faceAngle: [10],
	hand: [1, 4, 5, 6, 7, 8, 9, 10],
	helixAngle: [1, 2],
	leadAngle: [5, 6],
	pitchAngle: [3, 4],
	pressureAngle: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 11],
	ratio: [7, 8],
	rootAngle: [9],
	spiralAngle: [4, 9, 10],
}

export const handFieldLabelPerGearType = {
	1: 'Helix Hand',
	4: 'Gear Spiral Hand',
	5: 'Gear Hand',
	6: 'Gear Hand',
	7: 'Gear Hand',
	8: 'Gear Hand',
	9: 'Gear Spiral Hand',
	10: 'Gear Spiral Hand'
}

export const mapDriveOptionsPerGearType = {
	driven: [0, 1, 2, 3, 4, 6, 8, 9, 10, 11],
	driver: [0, 1, 2, 3, 4, 5, 7, 9, 10, 11],
}

/***/
export const getRequiredFieldsByGearType = (gearType: string) => {
	const globalRequiredFields: Array<keyof GearForceState> = [
		'directionRotation',
		'gearDriveOptions',
		'gearType',
		'meshAngle',
		'speed',
		'torque',
		'torqueOrPower',
		'unit',
		'workingDiameter',
	];

	const gearTypeId = Number(gearType);

	if (isNaN(gearTypeId)) return globalRequiredFields;

	// Find all keys in mapFieldsPerGearType that include gearTypeId
	const conditionalFields = Object.entries(mapFieldsPerGearType)
		.filter(([_, gearTypeIds]) => gearTypeIds.includes(gearTypeId))
		.map(([field]) => field as keyof GearForceState);

	return Array.from(new Set([...globalRequiredFields, ...conditionalFields]));
}

/***/
const getValidationMap = (gearType: string): GearForceValidationMap => {
	const requiredFields = getRequiredFieldsByGearType(gearType);

	const validationMap: GearForceValidationMap = new Map();

	requiredFields.forEach((field) => {
		validationMap.set(field, false); // default to false
	});

	return validationMap;
};


/**
 * Validate form data
 * The required fields depend on the current gearType
 */
export const validateFormData = (state: GearForceState): GearForceValidationMap => {
	const requiredFields = getRequiredFieldsByGearType(state.gearType ?? '');
	const validation: GearForceValidationMap = new Map();

	requiredFields.forEach((field) => {
		let isValid = false;
		const value = state[field];

		switch (field) {
			case 'concaveAngle':
			case 'convexAngle':
			case 'faceAngle':
			case 'helixAngle':
			case 'leadAngle':
			case 'meshAngle':
			case 'pitchAngle':
			case 'rootAngle':
			case 'spiralAngle':
				if (value == '') {
					isValid = false;
				} else {
					isValid = isValidDegAngle(Number(value));
				}
				break;

			case 'pressureAngle':
				isValid = Number(value) >= 15 && Number(value) <= 35;
				break;

			case 'directionRotation':
			case 'apex':
			case 'gearDriveOptions':
				isValid = value === '-1' || value === '1';
				break;

			case 'gearType':
				isValid = value !== '' && Number(value) >= 0 && Number(value) <= 11;
				break;

			case 'efficiency':
				isValid = Number(value) >= 0 && Number(value) <= 100;
				break;

			case 'hand':
				isValid = value === '-1' || value === '1';
				break;

			case 'torqueOrPower':
				isValid = value === '1' || value === '2';
				break;

			case 'workingDiameter':
			case 'ratio':
				isValid = Number(value) > 0;
				break;
			case 'speed':
			case 'torque':
				isValid = Number(value) >= 1;
				break;
			case 'centerDistance':
				isValid = Number(value) > (Number(state['workingDiameter']) / 2);
				break;
			default:
				isValid = value !== undefined && value !== null && value !== '';
		}

		validation.set(field, isValid);
	});

	return validation;
};

/**
 * note: global means the field is required for any gear types
 */
export const initialGearForceState: GearForceState = {
	apex: undefined,
	centerDistance: undefined,
	concaveAngle: undefined,
	convexAngle: undefined,
	directionRotation: undefined, // global
	efficiency: undefined,
	faceAngle: undefined,
	gearDriveOptions: undefined, // global
	gearName: '', // global
	gearType: '', // global
	hand: '-1',
	helixAngle: undefined,
	leadAngle: undefined,
	workingDiameter: undefined,  // global
	meshAngle: undefined, // global
	pitchAngle: undefined,
	pressureAngle: undefined,
	ratio: undefined,
	rootAngle: undefined,
	speed: undefined, // global
	spiralAngle: undefined,
	torque: undefined, // global
	torqueOrPower: '2', // global
	unit: 'M', // global
	validation: getValidationMap(''),
};


export type GearForceAction =
	| { type: 'change_gear_type'; payload: string }
	| { type: 'change_unit'; payload: TimkenUnit }
	| { type: 'update_value'; payload: Record<string, string> }
	| { type: 'validate_fields' }
	| { type: 'reset'; payload?: GearForceState
};

const gearForceReducer: React.Reducer<
	GearForceState,
	GearForceAction
> = (state, action) => {
	switch (action.type) {
		case 'change_gear_type': {
			return {
				...state,
				gearType: action.payload,
				validation: getValidationMap(action.payload),
			};
		}
		case 'change_unit': {
			return {
				...state,
				unit: action.payload,
			};
		}
		case 'update_value': {
			return {
				...state,
				...action.payload
			}
		}
		case 'validate_fields': {
			return {
				...state,
				validation: validateFormData(state),
			};
		}
		case 'reset': {
			const newState = action.payload ? action.payload : initialGearForceState;

			return {
				...newState
			};
		}
		default: {
			return handleActionError(action);
		}
	}

};

export default gearForceReducer;
